import { ApiError } from '@/api/client'
import {
  useCreateUser,
  useDeleteUser,
  useMe,
  useResetUserPassword,
  useUpdateUser,
  useUsers,
} from '@/api/hooks'
import type { AccountUser } from '@/api/types'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorNote, FormError, SkeletonRows } from '@/components/ui/Feedback'
import { Input, Select } from '@/components/ui/Field'
import { ConfirmDialog, Dialog } from '@/components/ui/Overlay'
import { useToast } from '@/components/ui/toastContext'
import { PageHeader, PageShell } from '@/components/layout/Page'
import { formatDate } from '@/lib/format'
import { useDocumentTitle } from '@/lib/useDocumentTitle'
import { Copy, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { useState } from 'react'

/** Admin user management. Accounts gate access; the recipe data stays shared. */
export function UsersPage() {
  useDocumentTitle('Users')
  const me = useMe()
  const users = useUsers()
  const update = useUpdateUser()
  const reset = useResetUserPassword()
  const remove = useDeleteUser()
  const toast = useToast()

  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<AccountUser | null>(null)
  const [issued, setIssued] = useState<{ username: string; password: string } | null>(null)

  const currentUser = me.data

  if (currentUser && currentUser.role !== 'admin') {
    return (
      <PageShell>
        <ErrorNote title="Admins only" body="Your account cannot manage other accounts." />
      </PageShell>
    )
  }

  if (users.isError) {
    return (
      <PageShell>
        <ErrorNote
          body={users.error instanceof ApiError ? users.error.message : undefined}
          onRetry={() => void users.refetch()}
        />
      </PageShell>
    )
  }

  async function toggleActive(user: AccountUser) {
    try {
      await update.mutateAsync({ id: user.id, is_active: !user.is_active })
      toast.success(
        user.is_active ? `${user.username} deactivated.` : `${user.username} reactivated.`,
      )
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not update.')
    }
  }

  async function changeRole(user: AccountUser, role: 'admin' | 'user') {
    try {
      await update.mutateAsync({ id: user.id, role })
      toast.success(`${user.username} is now ${role === 'admin' ? 'an admin' : 'a member'}.`)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not update.')
    }
  }

  async function resetPassword(user: AccountUser) {
    try {
      const result = await reset.mutateAsync(user.id)
      setIssued({
        username: result.user.username,
        password: result.temporary_password,
      })
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not reset.')
    }
  }

  async function confirmDelete() {
    if (!deleting) return
    try {
      await remove.mutateAsync(deleting.id)
      toast.success(`${deleting.username} deleted.`)
      setDeleting(null)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not delete.')
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Users"
        subtitle="Everyone with an account shares the same recipes, ingredients and exclusions."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus size={16} aria-hidden />
            New user
          </Button>
        }
      />

      <div className="space-y-4">
        <CreateUserDialog open={creating} onClose={() => setCreating(false)} />

        {issued ? (
          <Card className="border-caution/40">
            <CardHeader
              title="Temporary password issued"
              subtitle="Shown only once. Share it through a channel you trust."
            />
            <CardBody className="flex flex-wrap items-center gap-2">
              <code className="tabular flex-1 rounded-[var(--radius-field)] bg-surface-sunken px-3 py-2 font-mono text-sm text-ink">
                {issued.username} / {issued.password}
              </code>
              <Button
                variant="secondary"
                size="sm"
                onClick={async () => {
                  await navigator.clipboard.writeText(
                    `username: ${issued.username}\npassword: ${issued.password}`,
                  )
                  toast.success('Copied to the clipboard.')
                }}
              >
                <Copy size={13} aria-hidden />
                Copy
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setIssued(null)}>
                Done
              </Button>
            </CardBody>
          </Card>
        ) : null}

        {users.isPending ? (
          <SkeletonRows rows={4} />
        ) : (
          <Card className="overflow-hidden">
            <div className="scroll-slim overflow-x-auto">
              <table className="w-full min-w-[48rem] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface-sunken">
                    {['User', 'Role', 'Status', 'Created', ''].map((label) => (
                      <th
                        key={label}
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium text-ink-faint"
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(users.data ?? []).map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-line last:border-0 hover:bg-surface-sunken/70"
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span
                            aria-hidden
                            className="grid size-7 shrink-0 place-items-center rounded-full bg-surface-sunken text-micro font-semibold text-ink-soft"
                          >
                            {(user.display_name || user.username).charAt(0).toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-ink">
                              {user.display_name}
                            </p>
                            <p className="truncate text-micro text-ink-faint">
                              @{user.username}
                            </p>
                          </div>
                          {user.id === currentUser?.id ? (
                            <Badge tone="outline">You</Badge>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={user.role}
                          onChange={(event) =>
                            void changeRole(user, event.target.value as 'admin' | 'user')
                          }
                          wrapperClassName="w-32"
                          options={[
                            { value: 'admin', label: 'Admin' },
                            { value: 'user', label: 'Member' },
                          ]}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col items-start gap-1">
                          <Badge tone={user.is_active ? 'positive' : 'critical'}>
                            {user.is_active ? 'Active' : 'Deactivated'}
                          </Badge>
                          {user.must_change_password ? (
                            <Badge tone="caution">Temporary password</Badge>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-soft">
                        {formatDate(user.created_at.slice(0, 10))}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <IconButton
                            label={`Issue a new temporary password for ${user.username}`}
                            onClick={() => void resetPassword(user)}
                          >
                            <RotateCcw size={14} />
                          </IconButton>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={user.id === currentUser?.id}
                            onClick={() => void toggleActive(user)}
                          >
                            {user.is_active ? 'Deactivate' : 'Reactivate'}
                          </Button>
                          <IconButton
                            label={`Delete ${user.username}`}
                            variant="danger"
                            disabled={user.id === currentUser?.id}
                            onClick={() => setDeleting(user)}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
        loading={remove.isPending}
        destructive
        confirmLabel="Delete account"
        title={`Delete ${deleting?.username}?`}
        body="The account is removed. The recipes they created stay in the collection."
      />
    </PageShell>
  )
}

function CreateUserDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const create = useCreateUser()
  const toast = useToast()
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<'admin' | 'user'>('user')
  const [issued, setIssued] = useState<string | null>(null)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    try {
      const result = await create.mutateAsync({
        username: username.trim(),
        display_name: displayName.trim() || username.trim(),
        role,
      })
      setIssued(result.temporary_password)
      setUsername('')
      setDisplayName('')
      toast.success(`${result.user.username} created.`)
    } catch {
      // Shown below.
    }
  }

  const error = create.error

  function close() {
    setIssued(null)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      title="New user"
      description="A temporary password is generated and must be changed on first sign-in."
      size="sm"
      footer={
        issued ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={create.isPending}
              disabled={!username.trim()}
              onClick={(event) => {
                event.preventDefault()
                void submit(event as unknown as React.FormEvent)
              }}
            >
              Create user
            </Button>
          </>
        )
      }
    >
      {issued ? (
        <div className="space-y-2">
          <p className="text-sm text-ink-soft">
            Give {username.trim()} this password. It is shown once.
          </p>
          <code className="tabular block rounded-[var(--radius-field)] bg-surface-sunken px-3 py-2 font-mono text-sm text-ink">
            {issued}
          </code>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <FormError>
            {error instanceof ApiError ? error.message : null}
          </FormError>
          <Input
            label="Username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            data-autofocus
            required
          />
          <Input
            label="Display name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            placeholder="Defaults to the username"
          />
          <Select
            label="Role"
            value={role}
            onChange={(event) => setRole(event.target.value as 'admin' | 'user')}
            options={[
              { value: 'user', label: 'Member' },
              { value: 'admin', label: 'Admin' },
            ]}
          />
          <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
        </form>
      )}
    </Dialog>
  )
}
